import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building, Phone, Mail, MapPin, Package, CheckCircle, Star, Download, FileText, Clock, DollarSign, Shield, Truck, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

interface Fornecedor {
  id: number;
  nome_empresa: string;
  cnpj: string;
  responsavel: string;
  email: string;
  telefone: string;
  segmentos: string;
  marcas: string;
  tipos_equipamentos: string;
  tipos_pecas: string;
  area_cobertura: string;
  link_catalogo: string;
  observacoes: string;
  cidade: string;
  estado: string;
  avaliacao: number;
  total_pedidos: number;
  tempo_medio_resposta: string;
}

interface OrcamentoEscolhido {
  id: number;
  codigo_interno: string;
  valor: number;
  condicao_peca: string;
  garantia: string;
  tempo_entrega: string;
  frete_incluso: boolean;
  observacoes: string;
  arquivo: string;
  created_at: string;
}

interface Pedido {
  id: number;
  tipo_equipamento: string;
  marca: string;
  modelo: string;
  tipo_peca: string;
  descricao: string;
  status: string;
  created_at: string;
}

const FornecedorEscolhido: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [fornecedor, setFornecedor] = useState<Fornecedor | null>(null);
  const [orcamento, setOrcamento] = useState<OrcamentoEscolhido | null>(null);
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/cliente/login');
      return;
    }

    carregarDados();
  }, [navigate, id]);

  const carregarDados = async () => {
    setIsLoading(true);
    try {
      // Simular dados do fornecedor escolhido
      const fornecedorMock: Fornecedor = {
        id: 1,
        nome_empresa: 'Peças Express Ltda',
        cnpj: '12.345.678/0001-90',
        responsavel: 'João Silva',
        email: 'contato@pecasexpress.com.br',
        telefone: '(11) 99999-9999',
        segmentos: 'Escavadeiras, Tratores, Pá Carregadeiras',
        marcas: 'Caterpillar, Komatsu, Volvo, Hitachi',
        tipos_equipamentos: 'Escavadeiras, Tratores, Pá Carregadeiras, Retroescavadeiras',
        tipos_pecas: 'Motor, Hidráulica, Elétrica, Transmissão, Filtros',
        area_cobertura: 'Todo Brasil',
        link_catalogo: 'https://www.pecasexpress.com.br/catalogo',
        observacoes: 'Empresa especializada em peças para máquinas pesadas há mais de 15 anos. Atendimento 24h.',
        cidade: 'São Paulo',
        estado: 'SP',
        avaliacao: 4.8,
        total_pedidos: 1250,
        tempo_medio_resposta: '15 minutos'
      };

      const orcamentoMock: OrcamentoEscolhido = {
        id: 1,
        codigo_interno: 'CAT-320D-001',
        valor: 2500.00,
        condicao_peca: 'nova',
        garantia: '12 meses',
        tempo_entrega: '3 dias úteis',
        frete_incluso: true,
        observacoes: 'Peça original Caterpillar, garantia de fábrica. Entrega em todo Brasil.',
        arquivo: 'orcamento_pecasexpress.pdf',
        created_at: '2024-01-15T10:30:00Z'
      };

      const pedidoMock: Pedido = {
        id: 1,
        tipo_equipamento: 'Escavadeira',
        marca: 'Caterpillar',
        modelo: '320D',
        tipo_peca: 'Motor',
        descricao: 'Bomba de óleo para motor, necessito urgente para manutenção preventiva',
        status: 'finalizado',
        created_at: '2024-01-15T09:00:00Z'
      };

      setFornecedor(fornecedorMock);
      setOrcamento(orcamentoMock);
      setPedido(pedidoMock);
      setIsLoading(false);
    } catch (error) {
      toast.error('Erro ao carregar dados do fornecedor');
      setIsLoading(false);
    }
  };

  const handleDownloadArquivo = (arquivo: string) => {
    if (!arquivo) {
      toast.error('Arquivo não disponível');
      return;
    }

    // Simular download do arquivo
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,JVBERi0xLjQKJcOkw7zDtsO...`; // Base64 simulado
    link.download = arquivo;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Arquivo ${arquivo} baixado com sucesso!`);
  };

  const handleWhatsApp = (telefone: string) => {
    const numero = telefone.replace(/\D/g, '');
    const mensagem = encodeURIComponent('Olá! Gostaria de mais informações sobre o orçamento.');
    window.open(`https://wa.me/55${numero}?text=${mensagem}`, '_blank');
  };

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCondicaoText = (condicao: string) => {
    switch (condicao) {
      case 'nova':
        return 'Nova';
      case 'usada':
        return 'Usada';
      case 'recondicionada':
        return 'Recondicionada';
      default:
        return condicao;
    }
  };

  const renderStars = (avaliacao: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`h-5 w-5 ${i <= avaliacao ? 'text-yellow-500 fill-current' : 'text-gray-300'}`}
        />
      );
    }
    return stars;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white border-b-2 border-red sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red rounded-lg">
                  <Building className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-red uppercase">
                  PeçaJá
                </h1>
              </div>
              <button onClick={() => navigate('/cliente/dashboard')} className="btn btn-outline">
                Voltar
              </button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <h3 className="text-xl font-semibold text-gray-dark mb-2">Carregando...</h3>
            <p className="text-gray-medium">Buscando informações do fornecedor</p>
          </div>
        </main>
      </div>
    );
  }

  if (!fornecedor || !orcamento || !pedido) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-white border-b-2 border-red sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red rounded-lg">
                  <Building className="h-8 w-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-red uppercase">
                  PeçaJá
                </h1>
              </div>
              <button onClick={() => navigate('/cliente/dashboard')} className="btn btn-outline">
                Voltar
              </button>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <div className="text-center">
            <Package className="h-16 w-16 text-gray-medium mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-dark mb-2">Fornecedor não encontrado</h3>
            <p className="text-gray-medium">O fornecedor selecionado não foi encontrado.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b-2 border-red sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red rounded-lg">
                <Building className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-red uppercase">
                PeçaJá
              </h1>
            </div>
            <button onClick={() => navigate('/cliente/dashboard')} className="btn btn-outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Success Message */}
        <div className="card mb-8 bg-green-50 border-green-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-500 rounded-full">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-green-800">Orçamento Escolhido!</h3>
              <p className="text-green-700">Você escolheu o orçamento de {fornecedor.nome_empresa}</p>
            </div>
          </div>
        </div>

        {/* Fornecedor Info */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-dark flex items-center">
              <Building className="h-6 w-6 mr-2" />
              Informações do Fornecedor
            </h2>
            <div className="flex items-center gap-2">
              {renderStars(fornecedor.avaliacao)}
              <span className="text-sm text-gray-medium">({fornecedor.avaliacao})</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Informações Básicas */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-dark mb-4">Dados da Empresa</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-gray-medium mb-1">Nome da Empresa</p>
                    <p className="text-gray-dark font-semibold">{fornecedor.nome_empresa}</p>
                  </div>
                  <div>
                    <p className="text-gray-medium mb-1">CNPJ</p>
                    <p className="text-gray-dark">{fornecedor.cnpj}</p>
                  </div>
                  <div>
                    <p className="text-gray-medium mb-1">Responsável</p>
                    <p className="text-gray-dark">{fornecedor.responsavel}</p>
                  </div>
                  <div>
                    <p className="text-gray-medium mb-1">Localização</p>
                    <p className="text-gray-dark">{fornecedor.cidade} - {fornecedor.estado}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-dark mb-4">Contatos</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-gray-medium" />
                    <div>
                      <p className="text-gray-dark font-semibold">{fornecedor.telefone}</p>
                      <button
                        onClick={() => handleWhatsApp(fornecedor.telefone)}
                        className="text-sm text-red hover:text-red-700"
                      >
                        Abrir WhatsApp
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-gray-medium" />
                    <p className="text-gray-dark">{fornecedor.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-gray-medium" />
                    <p className="text-gray-dark">{fornecedor.area_cobertura}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Especializações */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-dark mb-4">Especializações</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-gray-medium mb-1">Segmentos</p>
                    <p className="text-gray-dark">{fornecedor.segmentos}</p>
                  </div>
                  <div>
                    <p className="text-gray-medium mb-1">Marcas</p>
                    <p className="text-gray-dark">{fornecedor.marcas}</p>
                  </div>
                  <div>
                    <p className="text-gray-medium mb-1">Tipos de Equipamentos</p>
                    <p className="text-gray-dark">{fornecedor.tipos_equipamentos}</p>
                  </div>
                  <div>
                    <p className="text-gray-medium mb-1">Tipos de Peças</p>
                    <p className="text-gray-dark">{fornecedor.tipos_pecas}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-dark mb-4">Estatísticas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-gray-light rounded-lg">
                    <div className="text-2xl font-bold text-red">{fornecedor.total_pedidos}</div>
                    <div className="text-sm text-gray-medium">Pedidos Atendidos</div>
                  </div>
                  <div className="text-center p-3 bg-gray-light rounded-lg">
                    <div className="text-2xl font-bold text-red">{fornecedor.tempo_medio_resposta}</div>
                    <div className="text-sm text-gray-medium">Tempo Médio</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {fornecedor.observacoes && (
            <div className="mt-6 p-4 bg-gray-light rounded-lg">
              <h4 className="font-semibold text-gray-dark mb-2">Observações</h4>
              <p className="text-gray-dark">{fornecedor.observacoes}</p>
            </div>
          )}
        </div>

        {/* Orçamento Escolhido */}
        <div className="card mb-8">
          <h2 className="text-2xl font-bold text-gray-dark mb-6 flex items-center">
            <CheckCircle className="h-6 w-6 mr-2 text-green-600" />
            Orçamento Escolhido
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Detalhes do Pedido */}
            <div>
              <h3 className="text-lg font-semibold text-gray-dark mb-4">Detalhes do Pedido</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-gray-medium mb-1">Equipamento</p>
                  <p className="text-gray-dark font-semibold">{pedido.tipo_equipamento} {pedido.marca} {pedido.modelo}</p>
                </div>
                <div>
                  <p className="text-gray-medium mb-1">Tipo de Peça</p>
                  <p className="text-gray-dark">{pedido.tipo_peca}</p>
                </div>
                <div>
                  <p className="text-gray-medium mb-1">Descrição</p>
                  <p className="text-gray-dark">{pedido.descricao}</p>
                </div>
                <div>
                  <p className="text-gray-medium mb-1">Data do Pedido</p>
                  <p className="text-gray-dark">{formatarData(pedido.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Detalhes do Orçamento */}
            <div>
              <h3 className="text-lg font-semibold text-gray-dark mb-4">Detalhes do Orçamento</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-gray-medium mb-1">Valor</p>
                  <p className="text-2xl font-bold text-red">{formatarValor(orcamento.valor)}</p>
                </div>
                <div>
                  <p className="text-gray-medium mb-1">Condição da Peça</p>
                  <p className="text-gray-dark">{getCondicaoText(orcamento.condicao_peca)}</p>
                </div>
                <div>
                  <p className="text-gray-medium mb-1">Garantia</p>
                  <p className="text-gray-dark">{orcamento.garantia}</p>
                </div>
                <div>
                  <p className="text-gray-medium mb-1">Tempo de Entrega</p>
                  <p className="text-gray-dark">{orcamento.tempo_entrega}</p>
                </div>
                <div>
                  <p className="text-gray-medium mb-1">Frete</p>
                  <p className="text-gray-dark">
                    {orcamento.frete_incluso ? 'Incluso no valor' : 'Por conta do cliente'}
                  </p>
                </div>
                {orcamento.codigo_interno && (
                  <div>
                    <p className="text-gray-medium mb-1">Código Interno</p>
                    <p className="text-gray-dark">{orcamento.codigo_interno}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {orcamento.observacoes && (
            <div className="mt-6 p-4 bg-gray-light rounded-lg">
              <h4 className="font-semibold text-gray-dark mb-2">Observações do Orçamento</h4>
              <p className="text-gray-dark">{orcamento.observacoes}</p>
            </div>
          )}

          {orcamento.arquivo && (
            <div className="mt-6 p-4 bg-gray-light rounded-lg">
              <h4 className="font-semibold text-gray-dark mb-3 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Arquivo do Orçamento
              </h4>
              <button
                onClick={() => handleDownloadArquivo(orcamento.arquivo)}
                className="btn btn-outline"
              >
                <Download className="h-4 w-4 mr-2" />
                Download da Proposta
              </button>
            </div>
          )}
        </div>

        {/* Ações */}
        <div className="card">
          <h2 className="text-2xl font-bold text-gray-dark mb-6 flex items-center">
            <Truck className="h-6 w-6 mr-2" />
            Próximos Passos
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gray-light rounded-lg">
              <Phone className="h-12 w-12 text-red mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-dark mb-2">Entre em Contato</h3>
              <p className="text-gray-medium mb-4">Ligue ou envie mensagem para finalizar a compra</p>
              <button
                onClick={() => handleWhatsApp(fornecedor.telefone)}
                className="btn btn-primary w-full"
              >
                <Phone className="h-4 w-4 mr-2" />
                WhatsApp
              </button>
            </div>

            <div className="text-center p-6 bg-gray-light rounded-lg">
              <Mail className="h-12 w-12 text-red mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-dark mb-2">Envie um Email</h3>
              <p className="text-gray-medium mb-4">Solicite mais informações por email</p>
              <a
                href={`mailto:${fornecedor.email}?subject=Orçamento Aprovado`}
                className="btn btn-outline w-full"
              >
                <Mail className="h-4 w-4 mr-2" />
                Enviar Email
              </a>
            </div>

            <div className="text-center p-6 bg-gray-light rounded-lg">
              <Download className="h-12 w-12 text-red mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-dark mb-2">Baixe a Proposta</h3>
              <p className="text-gray-medium mb-4">Tenha o documento em mãos</p>
              <button
                onClick={() => handleDownloadArquivo(orcamento.arquivo)}
                className="btn btn-outline w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FornecedorEscolhido; 